import { useCallback, useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { useDebounce } from "@/hooks/useDebounce";
import { mailListPath } from "../api";
import type { MailDirectionFilter, MailListView, MailMessage, MailSource, MailTaskFilter } from "../types";
import { DeleteMailDialog } from "./DeleteMailDialog";
import { MailListPane } from "./MailListPane";
import { MailReadingPane } from "./MailReadingPane";
import { useFillViewportHeight } from "../hooks/useFillViewportHeight";
import { useMailConversation } from "../hooks/useMailConversation";
import { useMailList } from "../hooks/useMailList";
import { useSelectedMailId } from "../hooks/useSelectedMailId";

/** Matches Tailwind's md breakpoint, where the list and reading pane sit side by side. */
const SIDE_BY_SIDE = "(min-width: 768px)";
const SEARCH_DELAY_MS = 300;
const SEARCH_MINIMUM_LENGTH = 2;
const NOTICE_DURATION_MS = 5000;

type MailViewProps = {
  source: MailSource;
};

/**
 * A mail-client style list and reading pane. Used for one customer's mail on the customer
 * dashboard, and for the signed-in user's own mail on the Emails page. Shows conversations by
 * default, as mail clients do, with a switch to separate emails.
 */
export const MailView = ({ source }: MailViewProps) => {
  const [searchText, setSearchText] = useState("");
  const [view, setView] = useState<MailListView>("conversations");
  const [direction, setDirection] = useState<MailDirectionFilter>("all");
  const [taskFilter, setTaskFilter] = useState<MailTaskFilter>("all");
  const search = useDebounce(searchText, SEARCH_DELAY_MS, SEARCH_MINIMUM_LENGTH);
  const list = useMailList(mailListPath(source), view, search, direction, taskFilter);
  const { selectedMailId, select } = useSelectedMailId();
  const { state: readingState, retry: retryReading } = useMailConversation(selectedMailId, view, source);
  const { ref: frameRef, height } = useFillViewportHeight<HTMLDivElement>();

  const [isListHidden, setIsListHidden] = useState(false);
  const [isReadingOnSmallScreen, setIsReadingOnSmallScreen] = useState(selectedMailId !== null);
  const [pendingDelete, setPendingDelete] = useState<MailMessage | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const spansCustomers = source.kind === "mine";
  const emptyDescription = spansCustomers
    ? "Emails you send or receive with your contacts appear here once your mailbox is connected in Account Settings."
    : "There are currently no emails for this customer.";

  const { items, hasMore, isLoading, isLoadingMore, loadMore, removeItems, reload } = list;

  // In the conversations view the selected email may be an older one, such as from a link, whose
  // conversation is listed under its newest email; the row is matched by conversation then.
  const openThreadId = view === "conversations" && readingState.status === "loaded"
    ? readingState.conversation.threadId
    : null;
  const selectedIndex = items.findIndex(mail =>
    mail.id === selectedMailId || (openThreadId !== null && mail.threadId === openThreadId));
  const selectedListId = selectedIndex >= 0 ? items[selectedIndex].id : null;

  // Opens the newest email when nothing is selected, as the legacy tab did. Not on small
  // screens, where opening it would hide the list the user has just arrived at.
  useEffect(() => {
    if (selectedMailId || isLoading || items.length === 0) return;
    if (!window.matchMedia(SIDE_BY_SIDE).matches) return;
    select(items[0].id);
  }, [selectedMailId, isLoading, items, select]);

  const open = useCallback((mailId: string) => {
    select(mailId);
    setIsReadingOnSmallScreen(true);
  }, [select]);

  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => setNotice(null), NOTICE_DURATION_MS);
    return () => window.clearTimeout(timer);
  }, [notice]);

  /**
   * After a delete, an email that leaves its conversation behind reloads the list, as the
   * conversation's row may now show another email or no longer match the filters. Otherwise the
   * row is dropped where it is, keeping the loaded pages, and the next email is opened.
   */
  const handleDeleted = useCallback((mailId: string) => {
    setNotice("Email deleted");

    const conversation = readingState.status === "loaded" ? readingState.conversation : null;
    const remaining = view === "conversations" && conversation
      ? conversation.messages.filter(message => message.id !== mailId)
      : [];

    if (conversation && remaining.length > 0) {
      void reload();
      if (selectedMailId === mailId) select(remaining[remaining.length - 1].id);
      else retryReading();
      return;
    }

    const isDeletedRow = (row: (typeof items)[number]) =>
      row.id === mailId || (view === "conversations" && conversation !== null && row.threadId === conversation.threadId);
    const index = items.findIndex(isDeletedRow);
    const neighbour = index >= 0 ? items[index + 1] ?? items[index - 1] ?? null : null;
    const leavesListEmpty = items.every(isDeletedRow);

    removeItems(isDeletedRow);
    select(neighbour?.id ?? null);
    if (!neighbour) setIsReadingOnSmallScreen(false);
    // With nothing left loaded the list would show as empty and hide "load more", stranding later pages.
    if (leavesListEmpty && hasMore) void reload();
  }, [readingState, view, items, hasMore, selectedMailId, reload, select, retryReading, removeItems]);

  return (
    <div
      ref={frameRef}
      style={{ height }}
      className="flex overflow-hidden rounded-lg border border-gray-200 bg-white"
    >
      <section
        aria-label="Email list"
        className={cn(
          "h-full w-full shrink-0 border-gray-200 md:w-80 md:border-r lg:w-96",
          isReadingOnSmallScreen && "hidden md:block",
          isListHidden && "md:hidden",
        )}
      >
        <MailListPane
          items={items}
          selectedMailId={selectedListId}
          searchText={searchText}
          activeSearch={search}
          view={view}
          direction={direction}
          taskFilter={taskFilter}
          showCustomers={spansCustomers}
          emptyDescription={emptyDescription}
          isLoading={isLoading}
          isLoadingMore={isLoadingMore}
          hasMore={hasMore}
          error={list.error}
          onSearchTextChange={setSearchText}
          onViewChange={setView}
          onDirectionChange={setDirection}
          onTaskFilterChange={setTaskFilter}
          onSelect={open}
          onNavigate={select}
          onLoadMore={loadMore}
          onRetry={list.reload}
        />
      </section>

      <section
        aria-label="Reading pane"
        className={cn("h-full min-w-0 flex-1", !isReadingOnSmallScreen && "hidden md:block")}
      >
        <MailReadingPane
          state={readingState}
          isListHidden={isListHidden}
          showCustomers={spansCustomers}
          notice={notice}
          onToggleList={() => setIsListHidden(hidden => !hidden)}
          onBackToList={() => setIsReadingOnSmallScreen(false)}
          onRetry={retryReading}
          onDelete={setPendingDelete}
        />
      </section>

      {pendingDelete && (
        <DeleteMailDialog
          key={pendingDelete.id}
          message={pendingDelete}
          onClose={() => setPendingDelete(null)}
          onDeleted={handleDeleted}
        />
      )}
    </div>
  );
};
